/**
 * The minimum shape required by the pure paginator. Additional properties are
 * copied to every emitted fragment so editor- and story-specific metadata is
 * retained without making this utility depend on either data model.
 */
export interface NarrationTextBlockLike {
  type: string;
  character?: string;
  content: string;
}

/** `pageIndex` is the zero-based target page; callbacks may ignore it. */
export type NarrationPageFits<T extends NarrationTextBlockLike> = (
  blocks: readonly T[],
  pageIndex: number
) => boolean;

export type NarrationFragmentBoundary =
  | 'paragraph'
  | 'sentence'
  | 'word'
  | 'grapheme'
  | 'source-end';

export interface NarrationOutputReference {
  pageIndex: number;
  blockIndex: number;
  fragmentIndex: number;
  startOffset: number;
  endOffset: number;
  boundary: NarrationFragmentBoundary;
}

export interface NarrationSourceMapEntry {
  sourceIndex: number;
  outputs: NarrationOutputReference[];
}

export interface NarrationPaginationResult<T extends NarrationTextBlockLike> {
  pages: T[][];
  sourceMap: NarrationSourceMapEntry[];
}

interface GraphemeSegment {
  value: string;
  start: number;
  end: number;
}

interface SplitCandidate {
  endOffset: number;
  boundary: Exclude<NarrationFragmentBoundary, 'source-end'>;
}

const hasVisibleContent = (content: string) => /\S/u.test(content);

const segmentIntoGraphemes = (content: string): GraphemeSegment[] => {
  const Segmenter = (Intl as typeof Intl & {
    Segmenter?: new (
      locale?: string | string[],
      options?: { granularity: 'grapheme' }
    ) => {
      segment: (value: string) => Iterable<{ segment: string; index: number }>;
    };
  }).Segmenter;

  if (typeof Segmenter === 'function') {
    return Array.from(
      new Segmenter(undefined, { granularity: 'grapheme' }).segment(content),
      ({ segment, index }) => ({
        value: segment,
        start: index,
        end: index + segment.length
      })
    );
  }

  // Production browsers expose Intl.Segmenter. This conservative fallback
  // keeps combining marks, variation selectors, emoji modifiers, and ZWJ
  // sequences attached when an older runtime calls this pure utility.
  const segments: GraphemeSegment[] = [];
  let offset = 0;
  let regionalIndicatorCount = 0;

  for (const codePoint of Array.from(content)) {
    const start = offset;
    offset += codePoint.length;
    const previous = segments[segments.length - 1];
    const isRegionalIndicator = /^\p{Regional_Indicator}$/u.test(codePoint);
    const attachesToPrevious = Boolean(previous) && (
      /^[\p{Mark}\uFE0E\uFE0F\u200D\p{Emoji_Modifier}]$/u.test(codePoint)
      || previous.value.endsWith('\u200D')
      || (isRegionalIndicator && regionalIndicatorCount % 2 === 1)
    );

    if (attachesToPrevious) {
      previous.value += codePoint;
      previous.end = offset;
    } else {
      segments.push({ value: codePoint, start, end: offset });
    }

    regionalIndicatorCount = isRegionalIndicator
      ? regionalIndicatorCount + 1
      : 0;
  }

  return segments;
};

const collectRegexEndOffsets = (content: string, pattern: RegExp) => {
  const offsets: number[] = [];
  let match: RegExpExecArray | null;
  pattern.lastIndex = 0;

  while ((match = pattern.exec(content)) !== null) {
    offsets.push(match.index + match[0].length);
    if (match[0].length === 0) pattern.lastIndex += 1;
  }

  return offsets;
};

const boundaryOffsets = (
  content: string,
  boundary: Exclude<NarrationFragmentBoundary, 'source-end'>
) => {
  if (boundary === 'paragraph') {
    return collectRegexEndOffsets(content, /\r?\n[\t ]*\r?\n(?:[\t ]*\r?\n)*/g);
  }

  if (boundary === 'sentence') {
    return collectRegexEndOffsets(
      content,
      /[.!?\u2026\u3002\uFF01\uFF1F]+["'\u201D\u2019\u00BB\uFF09)\]}]*(?:\s+|(?=\S))/gu
    );
  }

  if (boundary === 'word') {
    return collectRegexEndOffsets(content, /\s+/gu);
  }

  return segmentIntoGraphemes(content).map(segment => segment.end);
};

const cloneWithContent = <T extends NarrationTextBlockLike>(
  source: T,
  content: string
): T => ({ ...source, content });

const validSplitOffsets = (content: string, offsets: number[]) => Array.from(
  new Set(offsets)
)
  .filter(offset => (
    offset > 0
    && offset < content.length
    && hasVisibleContent(content.slice(0, offset))
    && hasVisibleContent(content.slice(offset))
  ))
  .sort((left, right) => right - left);

const findPreferredSplit = <T extends NarrationTextBlockLike>(
  source: T,
  remainingContent: string,
  currentPage: readonly T[],
  pageIndex: number,
  pageFits: NarrationPageFits<T>
): SplitCandidate | null => {
  const preference: Array<Exclude<NarrationFragmentBoundary, 'source-end'>> = [
    'paragraph',
    'sentence',
    'word',
    'grapheme'
  ];

  for (const boundary of preference) {
    const offsets = validSplitOffsets(
      remainingContent,
      boundaryOffsets(remainingContent, boundary)
    );

    for (const endOffset of offsets) {
      const fragment = cloneWithContent(
        source,
        remainingContent.slice(0, endOffset)
      );
      if (pageFits([...currentPage, fragment], pageIndex)) {
        return { endOffset, boundary };
      }
    }
  }

  return null;
};

/**
 * Splits ordered narration/dialogue blocks across pages using an authoritative
 * caller-supplied fit predicate. The predicate can invoke the deterministic
 * layout solver and real text measurer; this function only chooses text-safe
 * boundaries and records where every emitted fragment came from.
 *
 * Empty or whitespace-only source blocks produce an empty source-map entry and
 * no output fragment. A visible block that cannot fit even one grapheme on an
 * empty page throws instead of dropping text or looping indefinitely.
 */
export const paginateNarrationBlocks = <T extends NarrationTextBlockLike>(
  sourceBlocks: readonly T[],
  pageFits: NarrationPageFits<T>
): NarrationPaginationResult<T> => {
  const pages: T[][] = [];
  let currentPage: T[] = [];
  const sourceMap: NarrationSourceMapEntry[] = sourceBlocks.map((_, sourceIndex) => ({
    sourceIndex,
    outputs: []
  }));

  const flushCurrentPage = () => {
    if (currentPage.length === 0) return;
    pages.push(currentPage);
    currentPage = [];
  };

  const appendFragment = (
    source: T,
    sourceIndex: number,
    content: string,
    startOffset: number,
    endOffset: number,
    boundary: NarrationFragmentBoundary
  ) => {
    if (!hasVisibleContent(content)) {
      throw new Error(`Narration source block ${sourceIndex + 1} produced an empty fragment.`);
    }

    const outputs = sourceMap[sourceIndex].outputs;
    outputs.push({
      pageIndex: pages.length,
      blockIndex: currentPage.length,
      fragmentIndex: outputs.length,
      startOffset,
      endOffset,
      boundary
    });
    currentPage.push(cloneWithContent(source, content));
  };

  sourceBlocks.forEach((source, sourceIndex) => {
    const originalContent = String(source.content ?? '');
    if (!hasVisibleContent(originalContent)) return;

    let sourceOffset = 0;
    while (sourceOffset < originalContent.length) {
      const remainingContent = originalContent.slice(sourceOffset);
      const completeRemainder = cloneWithContent(source, remainingContent);

      if (pageFits([...currentPage, completeRemainder], pages.length)) {
        appendFragment(
          source,
          sourceIndex,
          remainingContent,
          sourceOffset,
          originalContent.length,
          'source-end'
        );
        sourceOffset = originalContent.length;
        continue;
      }

      // Keep an intact source block intact when it fits on a fresh page.
      if (currentPage.length > 0 && pageFits([completeRemainder], pages.length + 1)) {
        flushCurrentPage();
        continue;
      }

      const split = findPreferredSplit(
        source,
        remainingContent,
        currentPage,
        pages.length,
        pageFits
      );
      if (split) {
        appendFragment(
          source,
          sourceIndex,
          remainingContent.slice(0, split.endOffset),
          sourceOffset,
          sourceOffset + split.endOffset,
          split.boundary
        );
        sourceOffset += split.endOffset;
        flushCurrentPage();
        continue;
      }

      if (currentPage.length > 0) {
        flushCurrentPage();
        continue;
      }

      throw new Error(
        `Narration source block ${sourceIndex + 1} cannot fit even one grapheme on an empty page.`
      );
    }
  });

  flushCurrentPage();
  return { pages, sourceMap };
};
